import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule, NgIf, NgFor } from '@angular/common';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { ProductModel, ProductModelUtils } from '../shared/product.model';
import { LucideAngularModule, ArrowLeft, Info, Package, Pencil, Tag } from 'lucide-angular';
import { ProductVariantModel } from '../shared/product-variant.model';
import { DomSanitizer } from '@angular/platform-browser';
import { SecurityContext } from '@angular/core';
import { AuthService } from '@app/auth/auth.service';

@Component({
  selector: 'app-product-view',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    BadgeComponent,
    NgIf,
    NgFor,
    LucideAngularModule,
    TippyDirective,
  ],
  templateUrl: './product-view.component.html',
  styleUrl: './product-view.component.css'
})
export class ProductViewComponent {
  protected product!: ProductModel;
  protected descriptionHtml: string | null = null;
  protected destinedForText: string | null = null;
  protected readonly ProductModelUtils = ProductModelUtils;
  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly PackageIcon = Package;
  protected readonly TagIcon = Tag;
  protected readonly InfoIcon = Info;
  protected readonly EditIcon = Pencil;
  protected isAdmin = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
  ) {
    this.product = this.route.snapshot.data['product'];
    this.normalizeRelationships();
    this.descriptionHtml = this.buildDescriptionHtml(this.product.description);
    this.destinedForText = this.cleanText(this.product.destined_for);

    this.authService.getUser().subscribe({
      next: (user) => {
        this.isAdmin = user?.roles?.includes('admin') ?? false;
      },
      error: () => {
        this.isAdmin = false;
      },
    });
  }

  get heroImage(): string | null {
    if (this.product?.images && this.product.images.length > 0) {
      return this.product.images[0].lg || this.product.images[0].sm;
    }
    return this.product?.image_1_sm || null;
  }

  backToList(): void {
    this.router.navigate(['/products']);
  }

  editProduct(): void {
    if (!this.product?.id) {
      return;
    }

    this.router.navigate(['/products', this.product.id, 'edit']);
  }

  formatBoolean(value: boolean): string {
    return value ? 'Yes' : 'No';
  }

  hasVariants(): boolean {
    return this.product.variants.length > 0;
  }

  private normalizeRelationships(): void {
    this.product.variants = (this.product.variants || []) as ProductVariantModel[];
    this.product.variants.forEach((variant) => {
      variant.options = variant.options || [];
    });
  }

  private buildDescriptionHtml(value: string | null | undefined): string | null {
    if (!this.hasNonEmptyContent(value)) {
      return null;
    }
    const safeInput = value ?? null;
    // Angular will sanitize the HTML when bound with [innerHTML]; sanitize again to keep only safe markup.
    return this.sanitizer.sanitize(SecurityContext.HTML, safeInput) || null;
  }

  private hasNonEmptyContent(value: string | null | undefined): boolean {
    if (value === undefined || value === null) {
      return false;
    }
    const textOnly = value
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return !!textOnly && textOnly.toLowerCase() !== 'null' && textOnly !== '-';
  }

  private cleanText(value: string | null | undefined): string | null {
    if (value === undefined || value === null) {
      return null;
    }

    // Strip HTML and collapse whitespace so empty rich text like "<p> </p>" is treated as missing
    const trimmed = value
      .toString()
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed === '-') {
      return null;
    }

    return trimmed;
  }
}
